import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Send } from 'lucide-react';

const SectionContainer = styled.section`
  padding: 120px 0;
  background: #474545;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: left;
  }
`;

const TextContent = styled.div`
  @media (max-width: 968px) {
    text-align: left;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    text-align: left;
  }
`;

const TelegramButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 18px 36px;
  border-radius: 50px;
  font-family: 'Comfortaa', sans-serif;
  font-weight: 600;
  font-size: 18px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 151, 178, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 151, 178, 0.4);
    color: #FAECEC;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  max-width: 265px;
  margin: 0 auto;

  @media (max-width: 968px) {
    max-width: 235px;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  height: auto;
  filter: drop-shadow(0 20px 40px rgba(0, 151, 178, 0.4));
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
    filter: drop-shadow(0 25px 50px rgba(0, 151, 178, 0.5));
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 151, 178, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  z-index: 0;
  animation: pulse 4s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.5;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 0.8;
    }
  }
`;

const TelegramSection = () => {
  return (
    <SectionContainer id="telegram">
      <BackgroundGlow />
      <Container>
        <ContentWrapper>
          <TextContent>
            <SectionTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Telegram Mini-App
            </SectionTitle>
            
            <Subtitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Use directly in Telegram as a mobile application.
            </Subtitle>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <TelegramButton
                href="https://t.me/f01iapp_bot"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send size={24} color="#FAECEC" />
                Open @f01iapp_bot
              </TelegramButton>
            </motion.div>
          </TextContent>

          <ImageContainer
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <PhoneImage
              src="https://customer-assets.emergentagent.com/job_foliux-audit/artifacts/gidbgasr_Subject.png"
              alt="f01i.ai Telegram Mini-App"
            />
          </ImageContainer>
        </ContentWrapper>
      </Container>
    </SectionContainer>
  );
};

export default TelegramSection;
